import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { ArrowRight, ChevronRight, LucideAngularModule, Plus } from 'lucide-angular';
import { CustomerHomeDate, CustomerHomeJourney, CustomerHomeLine } from '@app/customer/shared/customer-home.model';
import { ContactJourneyService } from '@app/contacts/shared/contact-journey.service';
import { ContactJourneyItem } from '@app/contacts/shared/contact-journey.model';

/**
 * TVG-360: the contact's events and quotes, one line each.
 *
 * Replaces the three stacked journey bands (TVG-174) with the layout Mark
 * agreed on 21 October 2026: two lists, one line per event and one line per
 * quoted date, each reading across the width — day of the week, venue, stage,
 * guests and total — and each expanding downwards in place. Several lines can
 * be open at once and opening one never moves the rest of the page.
 *
 * What a line carries is deliberately not repeated inside it: an expanded event
 * shows one across-the-width strip (venue contact, sales manager, event
 * manager, internal notes), the breakdown and its two buttons;
 * an expanded quote shows one strip (venue contact and when it was sent), that
 * date's breakdown and one button. TVG-377: quotes from the same enquiry are
 * never grouped, so the strip no longer lists the enquiry's other dates. There is
 * no details box, no repeated venue name and no quick compare anywhere.
 *
 * Nothing about pricing, stage or permissions changed with the layout: the same
 * payload drives both, read a different way round.
 */

/**
 * One quote line — a single quoted date out of its enquiry. An enquiry quoted
 * for three dates is three lines, each carrying which of the three it is.
 */
export interface ContactQuoteLine {
  item: ContactJourneyItem;
  date: CustomerHomeDate;
  /** 1-based position of this date within its enquiry. */
  position: number;
  /** How many dates the enquiry was quoted for. */
  total: number;
}

@Component({
  selector: 'app-contact-journey',
  standalone: true,
  imports: [CommonModule, RouterLink, LucideAngularModule],
  templateUrl: './contact-journey.component.html',
})
export class ContactJourneyComponent implements OnChanges {
  protected readonly ArrowRightIcon = ArrowRight;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly PlusIcon = Plus;

  @Input({ required: true }) contactId!: number;

  /**
   * When true, a "Create Event" action shows in the Events header (admin/manager
   * on an existing contact, editable view).
   */
  @Input() canCreateEvent = false;

  /**
   * TVG-360: an expanded event offers "Book a viewing". The dialog lives on the
   * contact page so the Show rounds panel and this list book through exactly one
   * place; this only asks for it.
   */
  @Output() bookViewing = new EventEmitter<number>();

  /**
   * TVG-360 (eleven a): the contact's single sales journey, handed up so the
   * page can show it once above show rounds. It is read from the same payload
   * this panel already fetches rather than asked for again, and it is emitted
   * on every load (null included) so the summary never shows a stale journey
   * after something on the page has changed.
   */
  @Output() journeyLoaded = new EventEmitter<CustomerHomeJourney | null>();

  protected loading = true;
  protected loadFailed = false;
  protected items: ContactJourneyItem[] = [];

  protected expandedKeys = new Set<string>();

  constructor(
    private journeyService: ContactJourneyService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contactId'] && this.contactId) {
      this.load();
    }
  }

  /** Re-read the lists after something on the page has changed them. */
  reload(): void {
    this.load();
  }

  /**
   * TVG-173/TVG-360: progress one quoted date to a detailed quote — the Add
   * Event screen with that single date seeded (kept editable) plus the usual
   * quote prefills. The quote is left intact so other dates can be progressed
   * separately. Unchanged behaviour; only the button's wording moved to
   * "Progress to detailed quote" (TVG-329).
   */
  protected progressToDetailedQuote(line: ContactQuoteLine): void {
    const date = line.date.date;
    if (line.item.type !== 'quote' || !date) {
      return;
    }

    this.router.navigate(['/events/create'], {
      queryParams: {
        quoteId: line.item.id,
        eventDate: date,
      },
    });
  }

  /** Every event, confirmed ones first — the payload is already in that order. */
  protected get eventItems(): ContactJourneyItem[] {
    return this.items.filter((item) => item.group === 'confirmed' || item.group === 'draft');
  }

  /**
   * TVG-360/TVG-369: one line per quoted date, newest quote first. Every quote
   * that was sent is its own line, even when several dates came from one
   * enquiry, and a later quote on the same date with different numbers is its
   * own line too — it is a separate quote record, so it never folds into the
   * first. The payload orders items confirmed-first then by date, so quotes
   * are re-sorted here by when they were actually sent.
   */
  protected get quoteLines(): ContactQuoteLine[] {
    const lines: ContactQuoteLine[] = [];

    const quotes = this.items
      .filter((item) => item.group === 'quote')
      .sort((a, b) => this.receivedAtTimestamp(b) - this.receivedAtTimestamp(a));

    for (const item of quotes) {
      const dates = this.quotedDates(item);
      dates.forEach((date, index) => {
        // TVG-381: a date that became a detailed quote is on that line
        // instead, the same rule the client home follows.
        if (item.progressed_date && date.date === item.progressed_date) {
          return;
        }
        lines.push({ item, date, position: index + 1, total: dates.length });
      });
    }

    return lines;
  }

  private receivedAtTimestamp(item: ContactJourneyItem): number {
    if (!item.received_at) {
      return 0;
    }
    const parsed = new Date(item.received_at).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  protected get hasAnything(): boolean {
    return this.items.length > 0;
  }

  protected eventKey(item: ContactJourneyItem): string {
    return `${item.type}-${item.id}`;
  }

  protected quoteKey(line: ContactQuoteLine): string {
    return `quote-${line.item.id}-${line.position}`;
  }

  protected toggle(key: string): void {
    if (this.expandedKeys.has(key)) {
      this.expandedKeys.delete(key);
    } else {
      this.expandedKeys.add(key);
    }
  }

  protected isExpanded(key: string): boolean {
    return this.expandedKeys.has(key);
  }

  /**
   * The breakdown shown inside an expanded event: the quoted date that matches
   * the event's own date, falling back to the first priced date when a folded
   * quote carries several and none matches exactly.
   */
  protected eventDate(item: ContactJourneyItem): CustomerHomeDate | null {
    // TVG-381: the server now names the date, for this page and the client
    // home alike; the fallback below covers an older payload.
    if (item.detailed_quote !== undefined) {
      return item.detailed_quote;
    }
    const dates = item.detail?.dates ?? [];
    if (dates.length === 0) {
      return null;
    }

    return dates.find((date) => date.date === item.date) ?? dates[0];
  }

  protected lineUnitTimesQuantity(unitPrice: number | null, quantity: number | null): string {
    if (unitPrice === null || quantity === null) {
      return '—';
    }
    return `${this.formatPrice(unitPrice)} × ${quantity}`;
  }

  protected formatPrice(value: number | null): string {
    if (value === null || Number.isNaN(value)) {
      return '—';
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  /** Short form, used where a date is secondary — "14 Oct 2026". */
  protected formatShortDate(value: string | null): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return parsed.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  /** Short form with the time, for when a quote was sent — "14 Oct 2026, 09:12". */
  protected formatDateTime(value: string | null): string {
    if (!value) {
      return '—';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return `${this.formatShortDate(value)}, ${parsed.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  /**
   * TVG-330/TVG-360: every date on a line reads with its day of the week in
   * front — "Saturday 12 June 2027". The weekday is derived from the date
   * itself rather than any stored day_of_week, which older quotes can be
   * missing entirely.
   */
  protected formatLongDate(value: string | null): string {
    if (!value) {
      return 'Date TBC';
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    const parts = new Intl.DateTimeFormat('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).formatToParts(parsed);
    const part = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';

    return `${part('weekday')} ${part('day')} ${part('month')} ${part('year')}`.trim();
  }

  /** Gentle demand prompt for a quoted date — how many other people have enquired. */
  protected demandLabel(count: number): string | null {
    if (!count || count < 1) {
      return null;
    }
    return count === 1
      ? '1 other enquiry for this date'
      : `${count} other enquiries for this date`;
  }

  /**
   * TVG-369: the "Date availability" tri-state — null (no badge) while we have
   * no read on the date, "Available" when it's free, and "Taken · Prov"/"Taken
   * · Conf" when another event already holds it, coloured by how firm that
   * hold is.
   */
  protected availabilityBadge(date: CustomerHomeDate): { label: string; tone: 'blue' | 'amber' | 'green' } | null {
    if (date.available === true) {
      return { label: 'Available', tone: 'blue' };
    }
    if (date.available === false && date.blocking_status === 'Provisional') {
      return { label: 'Taken · Prov', tone: 'amber' };
    }
    if (date.available === false && date.blocking_status === 'Confirmed') {
      return { label: 'Taken · Conf', tone: 'green' };
    }
    return null;
  }

  /**
   * TVG-369: the "Date interest" tag — purely informational, independent of
   * availability, so it is hidden only once the date itself already reads
   * Taken (there is no point flagging interest in a date that's gone).
   */
  protected interestLabel(date: CustomerHomeDate): string | null {
    if (this.availabilityBadge(date)?.label.startsWith('Taken')) {
      return null;
    }
    const count = date.other_enquiries;
    if (!count || count < 1) {
      return null;
    }
    return count === 1 ? '1 other' : `${count} others`;
  }

  /**
   * TVG-369: the breakdown's Guests column split into Day/Evening — a day item
   * (Wedding breakfast, Drinks package) carries the day guest count, an evening
   * item (Evening food) carries the evening count. Venue hire carries neither.
   */
  protected lineGuestCounts(line: CustomerHomeLine): { day: number | null; evening: number | null } {
    if (line.quantity === null) {
      return { day: null, evening: null };
    }
    return line.category === 'Evening food'
      ? { day: null, evening: line.quantity }
      : { day: line.quantity, evening: null };
  }

  /**
   * TVG-317: every date the quote was built for. A quote with no priced
   * comparison falls back to its own headline date, so it never loses its date
   * entirely and still earns exactly one line.
   */
  private quotedDates(item: ContactJourneyItem): CustomerHomeDate[] {
    const dates = item.detail?.dates ?? [];
    if (dates.length > 0) {
      return dates;
    }

    return [
      {
        date: item.date,
        day_of_week: null,
        total: item.headline_price,
        available: null,
        blocking_status: null,
        other_enquiries: 0,
        is_preferred: false,
        budget_state: null,
        lines: [],
      },
    ];
  }

  private load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.journeyService.getJourney(this.contactId).subscribe({
      next: (response) => {
        this.items = response.data.items;
        this.loading = false;
        this.journeyLoaded.emit(response.data.contact?.journey ?? null);
      },
      error: () => {
        this.loadFailed = true;
        this.loading = false;
        this.journeyLoaded.emit(null);
      },
    });
  }
}
